import React, { useEffect, useState, useMemo } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { tolerantId } from "../../utils/firestoreHelpers";
import ScreenHeader from "../../components/ScreenHeader";
import { FaEye, FaArrowLeft, FaBuilding, FaKey, FaBars } from "react-icons/fa";
import { formatDocumentoDisplay } from "../../utils/documentoFormat";
import {
  colors,
  shadows,
  radius,
  spacing,
  typography,
  transitions,
  cardBase,
  btnPrimary,
  btnSecondary,
  btnDanger,
  btnGhost,
  inputBase,
  pageContainer,
  pageTitle,
  tableHeader,
  tableCell,
  modalOverlay,
} from "../../utils/designTokens";

/** Mismas funcionalidades que PermisosAdmin.jsx (solo lectura) */
const PERMISOS_FEATURES = [
  "Ver Calendario",
  "Gestionar Edificios",
  "Editar Unidades",
  "Ver Usuarios",
  "Gestionar Administradores",
  "Marcaciones",
  "Resultados",
  "Tienda",
  "Expensas",
  "Amenities",
  "Proveedores",
  "Reportes",
];

// Labels visibles: "Expensas" (key persistida) -> "Gastos Comunes" (display).
const PERMISOS_FEATURE_LABELS = {
  "Expensas": "Gastos Comunes",
};

/** Réplica del menú lateral del panel admin (referencia para soporte) */
const MENU_ADMIN_REFERENCIA = [
  { seccion: "General", items: [
    { id: "homeadmin", label: "Inicio" },
    { id: "edificios", label: "Mis Edificios" },
  ]},
  { seccion: "Residentes", items: [
    { id: "gestionusuarios", label: "Usuarios" },
    { id: "onboardingresidente", label: "Onboarding" },
  ]},
  { seccion: "Operaciones", items: [
    { id: "pagosedificio", label: "Gastos" },
    { id: "incidenciasedificio", label: "Incidencias" },
    { id: "novedadesedificio", label: "Novedades" },
    { id: "proveedoresedificio", label: "Proveedores" },
  ]},
  { seccion: "Edificio", items: [
    { id: "asambleas", label: "Asambleas" },
    { id: "proyectosadmin", label: "Obras" },
    { id: "revisionesadmin", label: "Revisiones" },
    { id: "documentacionadmin", label: "Documentos" },
    { id: "reservasamenities", label: "Reservas Amenities" },
  ]},
  { seccion: "Empleados", items: [
    { id: "asistenciaempleados", label: "Asistencia" },
    { id: "chatadmin", label: "Chat Empleados" },
    { id: "solicitudesadmin", label: "Licencias" },
    { id: "recibosadmin", label: "Liquidaciones" },
    { id: "tareasadmin", label: "Tareas" },
    { id: "turnosadmin", label: "Turnos" },
  ]},
  { seccion: "Configuración", items: [
    { id: "cierredemes", label: "Cierre de Mes" },
    { id: "revisioncierres", label: "Revisión de cierres" },
  ]},
];

async function fetchBuildingsForAdminUserid(userid) {
  const adminSnap = await getDocs(
    query(collection(db, "admin"), where("userid", "in", tolerantId(userid)))
  );
  const adminDocs = adminSnap.docs;
  const wantedIds = adminDocs.map((d) => d.data().buildingid);
  if (wantedIds.length === 0) return [];

  const wantedStr = new Set(wantedIds.map((x) => String(x)));
  const wantedNum = new Set(
    wantedIds.filter((x) => !isNaN(Number(x))).map((x) => Number(x))
  );

  const snap = await getDocs(collection(db, "building"));
  const out = [];
  snap.forEach((docu) => {
    const data = docu.data();
    const bid = data.buildingid;
    const bs = String(bid);
    const bn = Number(bid);
    if (wantedStr.has(bs) || (!isNaN(bn) && wantedNum.has(bn))) {
      out.push({
        docId: docu.id,
        buildingid: bid,
        name: data.name || "—",
        address: data.address || "—",
        city: data.city || "—",
        adminid: data.adminid,
      });
    }
  });
  out.sort((a, b) => Number(a.buildingid) - Number(b.buildingid));
  return out;
}

/**
 * Vista de soporte (solo superadmin): réplica informativa de lo que ve un administrador.
 * No inicia sesión como ese usuario; solo muestra datos de Firestore.
 */
const AdminVistaSoporte = ({
  setSelected,
  adminUserDocId,
  clearAdminVista,
  onEnterPanelPreview,
}) => {
  const [loading, setLoading] = useState(true);
  const [userRow, setUserRow] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [permValues, setPermValues] = useState({});

  const defaultPerms = useMemo(() => {
    const base = {};
    PERMISOS_FEATURES.forEach((f) => {
      base[f] = { permiso: false };
    });
    return base;
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!adminUserDocId) {
        setLoading(false);
        setUserRow(null);
        setBuildings([]);
        setPermValues({});
        return;
      }
      setLoading(true);
      try {
        const userSnap = await getDoc(doc(db, "users", adminUserDocId));
        if (cancelled) return;
        if (!userSnap.exists()) {
          setUserRow(null);
          setBuildings([]);
          setPermValues(defaultPerms);
          return;
        }
        const u = userSnap.data();
        setUserRow({ id: userSnap.id, ...u });

        const uid = u.userid;
        const b = await fetchBuildingsForAdminUserid(uid);
        if (cancelled) return;
        setBuildings(b);

        const permSnap = await getDoc(doc(db, "admin_permissions", adminUserDocId));
        let merged = { ...defaultPerms };
        if (permSnap.exists()) {
          const fromDb = permSnap.data()?.permisos || {};
          PERMISOS_FEATURES.forEach((f) => {
            merged[f] = { permiso: !!fromDb[f]?.permiso };
          });
        }
        if (cancelled) return;
        setPermValues(merged);
      } catch (e) {
        console.error("AdminVistaSoporte:", e);
        if (!cancelled) {
          setUserRow(null);
          setBuildings([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [adminUserDocId, defaultPerms]);

  const handleBack = () => {
    if (clearAdminVista) clearAdminVista();
    setSelected("gestionadmins");
  };

  if (!adminUserDocId) {
    return (
      <div style={styles.page}>
        <ScreenHeader
          icon={FaEye}
          title="Vista de administrador"
          subtitle="Soporte"
          onBack={handleBack}
        />
        <p style={styles.muted}>
          No hay un administrador seleccionado. Volvé a la lista y tocá el ícono del ojo.
        </p>
      </div>
    );
  }

  const displayName =
    userRow?.name ||
    [userRow?.NombreCompleto, userRow?.ApellidosCompleto].filter(Boolean).join(" ") ||
    userRow?.email ||
    "—";

  return (
    <div style={styles.page}>
      <ScreenHeader
        icon={FaEye}
        title="Vista de administrador"
        subtitle="Solo lectura · para soporte"
        onBack={handleBack}
      />

      <div style={styles.banner}>
        <FaEye size={16} color={colors.warningDark} />
        <span>
          Resumen de edificios y permisos (solo lectura). Podés abrir abajo la vista previa del panel
          con el mismo menú y datos que vería ese administrador, sin usar su cuenta.
        </span>
      </div>

      {loading ? (
        <p style={styles.muted}>Cargando…</p>
      ) : !userRow ? (
        <p style={styles.error}>No se encontró el usuario.</p>
      ) : (
        <>
          <section style={styles.section}>
            <h3 style={styles.h3}>Datos del administrador</h3>
            <div style={styles.card}>
              <div style={styles.kv}><span style={styles.k}>Nombre</span><span>{displayName}</span></div>
              <div style={styles.kv}><span style={styles.k}>Correo</span><span>{userRow.email || "—"}</span></div>
              <div style={styles.kv}><span style={styles.k}>userid</span><span>{String(userRow.userid ?? "—")}</span></div>
              <div style={styles.kv}><span style={styles.k}>Documento</span><span>{formatDocumentoDisplay(userRow)}</span></div>
              <div style={styles.kv}><span style={styles.k}>Celular</span><span>{userRow.celular || "—"}</span></div>
            </div>
            {onEnterPanelPreview && userRow.userid != null && userRow.email && (
              <div style={{ marginTop: 16 }}>
                <button
                  type="button"
                  style={styles.btnPreview}
                  onClick={() =>
                    onEnterPanelPreview({
                      userid: userRow.userid,
                      email: String(userRow.email || "").trim().toLowerCase(),
                      name: displayName,
                    })
                  }
                >
                  Abrir vista previa del panel (como este admin)
                </button>
                <p style={{ ...styles.hint, marginTop: 8, marginBottom: 0 }}>
                  Se abre el panel de administración con sus edificios y menú. Usá «Salir de la vista» arriba para volver.
                </p>
              </div>
            )}
          </section>

          <section style={styles.section}>
            <h3 style={styles.h3}>
              <FaBuilding size={16} color={colors.primary} style={{ marginRight: 8 }} />
              Edificios que ve en «Mis edificios»
            </h3>
            <p style={styles.hint}>
              Según la tabla <code style={styles.code}>admin</code> vinculada a su <code style={styles.code}>userid</code>.
            </p>
            {buildings.length === 0 ? (
              <p style={styles.muted}>No tiene edificios asignados.</p>
            ) : (
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>ID</th>
                      <th style={styles.th}>Nombre</th>
                      <th style={styles.th}>Dirección</th>
                      <th style={styles.th}>Ciudad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buildings.map((b) => (
                      <tr key={b.docId}>
                        <td style={styles.td}>{String(b.buildingid)}</td>
                        <td style={styles.td}>{b.name}</td>
                        <td style={styles.td}>{b.address}</td>
                        <td style={styles.td}>{b.city}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section style={styles.section}>
            <h3 style={styles.h3}>
              <FaKey size={16} color={colors.primary} style={{ marginRight: 8 }} />
              Permisos (misma grilla que «Permisos admin»)
            </h3>
            <p style={styles.hint}>
              Si todo figura como «No», en la app suele denegarse el acceso a esas funciones hasta que un superadmin los active.
            </p>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Funcionalidad</th>
                    <th style={styles.th}>Permitido</th>
                  </tr>
                </thead>
                <tbody>
                  {PERMISOS_FEATURES.map((f) => (
                    <tr key={f}>
                      <td style={styles.td}>{PERMISOS_FEATURE_LABELS[f] || f}</td>
                      <td style={styles.td}>
                        {permValues[f]?.permiso ? (
                          <span style={styles.si}>Sí</span>
                        ) : (
                          <span style={styles.no}>No</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section style={styles.section}>
            <h3 style={styles.h3}>
              <FaBars size={16} color={colors.primary} style={{ marginRight: 8 }} />
              Menú lateral (referencia)
            </h3>
            <p style={styles.hint}>
              Pantallas del panel de administración. La visibilidad real puede depender de los permisos de arriba y del edificio seleccionado.
            </p>
            {MENU_ADMIN_REFERENCIA.map((block) => (
              <div key={block.seccion} style={styles.menuBlock}>
                <div style={styles.menuSecTitle}>{block.seccion}</div>
                <ul style={styles.ul}>
                  {block.items.map((it) => (
                    <li key={it.id} style={styles.li}>
                      <code style={styles.codeSmall}>{it.id}</code>
                      <span style={styles.menuLabel}>{it.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>

          <button type="button" style={styles.backLink} onClick={handleBack}>
            <FaArrowLeft size={12} /> Volver a administradores
          </button>
        </>
      )}
    </div>
  );
};

// Estilos migrados a design tokens
const styles = {
  page: {
    padding: spacing[6],
    maxWidth: 960,
    margin: "0 auto",
    fontFamily: typography.fontFamily,
  },
  // Banner de advertencia
  banner: {
    display: "flex",
    gap: spacing[3],
    alignItems: "flex-start",
    padding: "12px 16px",
    background: colors.warningLight,
    borderRadius: radius.md,
    boxShadow: shadows.xs,
    color: colors.warningDark,
    fontSize: typography.sizes.md,
    lineHeight: 1.5,
    marginBottom: spacing[6],
  },
  section: { marginBottom: 28 },
  h3: {
    margin: "0 0 12px 0",
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.gray900,
    display: "flex",
    alignItems: "center",
  },
  hint: { margin: "0 0 12px 0", fontSize: typography.sizes.base, color: colors.gray600, lineHeight: 1.45 },
  // Tarjeta de datos — sin borde, solo sombra
  card: {
    ...cardBase,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  kv: { display: "flex", gap: spacing[3], flexWrap: "wrap", fontSize: typography.sizes.md },
  k: { color: colors.gray600, minWidth: 100 },
  // Contenedor de tabla — sin borde, solo sombra
  tableWrap: {
    overflowX: "auto",
    borderRadius: radius.md,
    boxShadow: shadows.sm,
    background: colors.white,
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: typography.sizes.base },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    background: colors.gray50,
    borderBottom: `1px solid ${colors.gray200}`,
    color: colors.gray900,
    fontWeight: 400,
  },
  td: {
    padding: "10px 12px",
    borderBottom: `1px solid ${colors.gray100}`,
    color: colors.gray900,
  },
  si: { color: colors.success, fontWeight: 400 },
  no: { color: colors.gray400 },
  muted: { color: colors.gray600, fontSize: typography.sizes.md },
  error: { color: colors.error },
  code: { fontSize: typography.sizes.sm, background: colors.gray100, padding: "2px 6px", borderRadius: 4 },
  codeSmall: { fontSize: typography.sizes.xs, background: colors.gray100, padding: "2px 6px", borderRadius: 4, color: colors.gray700 },
  menuBlock: { marginBottom: spacing[4] },
  menuSecTitle: {
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    color: colors.primary,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: spacing[2],
  },
  ul: { margin: 0, paddingLeft: 0, listStyle: "none" },
  li: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "6px 0",
    borderBottom: `1px solid ${colors.gray100}`,
    fontSize: typography.sizes.base,
  },
  menuLabel: { color: colors.gray900 },
  btnPreview: {
    ...btnPrimary,
    borderRadius: radius.md,
    padding: "12px 18px",
  },
  backLink: {
    marginTop: spacing[2],
    display: "inline-flex",
    alignItems: "center",
    gap: spacing[2],
    padding: "10px 0",
    background: "none",
    border: "none",
    color: colors.primary,
    cursor: "pointer",
    fontSize: typography.sizes.md,
    fontWeight: 400,
  },
};

export default AdminVistaSoporte;
